'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeferredIntercomInit } from "../hooks/useDeferredIntercomInit";
import { ErrorBoundary } from "react-error-boundary";
import { SettingsProvider } from "./settings";
import { LayerSwapAppSettings } from "../Models/LayerSwapAppSettings";
import ErrorFallback from "../components/ErrorFallback";
import { THEME_COLORS } from "../Models/Theme";
import { AsyncModalProvider } from "./asyncModal";
import { IntercomProvider } from 'react-use-intercom';
import AppSettings from "../lib/AppSettings";
import { getSettings } from "../helpers/getSettings";
import LayerSwapApiClient from "../lib/apiClients/layerSwapApiClient";
import ColorSchema from "../components/ColorSchema";
import { WidgetLoading } from "../components/WidgetLoading";
import WalletsProviders from "../components/Wallet/WalletProviders";
import { CallbackProvider } from "./callbackProvider";
import { SwapAccountsProvider } from "./swapAccounts";
import { isWalletProviderDescriptor } from "../types";
import { ResolverProviders, extractExtendedRouteProviders } from "./resolverContext";
import { setExtendedRouteProviders } from "../lib/extendedRoutes";
import { ErrorProvider } from "./ErrorProvider";
import { WalletDescriptorLoaderContext } from "../lib/walletConnect/walletDescriptorLoader";
const INTERCOM_APP_ID = 'h5zisg78';
// Stable identity when the prop is omitted — an inline `= []` default would
// mint a fresh array per render and defeat the memos keyed on `walletProviders`.
const NO_PROVIDERS = [];
// Core widget state is process-global (LayerSwapApiClient.apiKey, AppSettings,
// the extended-route registry, resolverService), so two live widget roots
// would silently rewrite one another's API key, settings, and providers.
// Until that state is instance-scoped, enforce one live LayerswapProvider per
// page: the second concurrent instance renders an explicit failure instead of
// cross-contaminating the first.
let liveProviderInstances = 0;
const LayerswapProviderComponent = ({ children, callbacks, config, walletProviders = NO_PROVIDERS }) => {
    let { apiKey, version, settings: _settings, theme, initialValues, loadingComponent, apiUri, imtblPassport, tonConfigs, walletConnect } = config || {};
    const [fetchedSettings, setFetchedSettings] = useState(null);
    const [settingsError, setSettingsError] = useState(null);
    const [settingsFetchAttempt, setSettingsFetchAttempt] = useState(0);
    const [duplicateMount, setDuplicateMount] = useState(false);
    // Registered in an effect (not render) so StrictMode's mount → cleanup →
    // remount cycle balances out and abandoned renders never count.
    useEffect(() => {
        liveProviderInstances++;
        if (liveProviderInstances > 1) {
            console.error('[layerswap/widget] Multiple LayerswapProvider instances detected. '
                + 'The widget keeps process-global state, so only one widget root may be live per page. '
                + 'This instance will not render; destroy the existing widget first.');
            setDuplicateMount(true);
        }
        return () => {
            liveProviderInstances--;
        };
    }, []);
    // Defer Intercom script injection until the browser is idle. The provider
    // stays mounted so its context is always available (no remount of any
    // subtree); only the network/parse cost of the third-party widget is
    // pushed past first paint.
    const intercomReady = useDeferredIntercomInit();
    const themeData = useMemo(() => ({ ...THEME_COLORS['default'], ...(theme ?? {}) }), [theme]);
    // Legacy globals are read synchronously by descendants during render.
    AppSettings.ApiVersion = version || AppSettings.ApiVersion;
    AppSettings.LayerswapApiUri = apiUri || AppSettings.LayerswapApiUri;
    LayerSwapApiClient.apiBaseEndpoint = AppSettings.LayerswapApiUri;
    AppSettings.ImtblPassportConfig = imtblPassport;
    AppSettings.TonClientConfig = tonConfigs || AppSettings.TonClientConfig;
    AppSettings.WalletConnectConfig = walletConnect || AppSettings.WalletConnectConfig;
    AppSettings.ThemeData = themeData;
    if (apiKey)
        LayerSwapApiClient.apiKey = apiKey;
    useEffect(() => {
        if (_settings) {
            setFetchedSettings(null);
            setSettingsError(null);
            return;
        }
        let cancelled = false;
        const apiVersion = version || AppSettings.ApiVersion;
        const settingsApiKey = apiKey || AppSettings.LayerswapApiKeys[apiVersion];
        void (async () => {
            // Failures land in state, not a throw — an async throw here is an
            // unhandled rejection the ErrorBoundary never sees, leaving the
            // widget on the loading skeleton forever.
            try {
                const fetchedSettings = await getSettings(settingsApiKey);
                if (!fetchedSettings)
                    throw new Error('Failed to fetch settings');
                if (!cancelled)
                    setFetchedSettings(fetchedSettings);
            }
            catch (error) {
                if (!cancelled)
                    setSettingsError(error instanceof Error ? error : new Error(String(error)));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [_settings, apiKey, version, settingsFetchAttempt, apiUri]);
    const retrySettingsFetch = useCallback(() => {
        setSettingsError(null);
        setSettingsFetchAttempt(attempt => attempt + 1);
    }, []);
    const settings = _settings || fetchedSettings;
    // Deliberate render-phase write: the registry must be populated before the
    // `appSettings` memo below runs (`mergeExtendedSourceNetworks` reads it), and
    // a post-commit effect is too late for the first settings object. Safe
    // because the assignment is idempotent and keyed on `walletProviders`, and
    // the single-live-instance guard above rules out a concurrent provider
    // publishing a different list.
    //
    // Registration is keyed on `walletProviders` alone — NOT on `settings`.
    // A settings-only identity change must not re-run it: this eager list lacks
    // descriptor-carried providers, so re-asserting it here would clobber the
    // fuller registry that `ResolverProviders`' effect publishes after descriptor
    // hydration (its deps wouldn't change, so it would never re-register). When
    // `walletProviders` itself changes, that same effect re-fires and restores
    // the full list post-commit.
    const extendedRouteProviders = useMemo(() => {
        const providers = extractExtendedRouteProviders(walletProviders);
        setExtendedRouteProviders(providers);
        return providers;
    }, [walletProviders]);
    const appSettings = useMemo(() => {
        if (!settings)
            return null;
        return new LayerSwapAppSettings(settings);
    }, [settings, extendedRouteProviders]);
    if (duplicateMount) {
        return (_jsx("div", { role: "alert", style: { padding: '1rem', textAlign: 'center' }, children: _jsx("p", { children: 'Only one Layerswap widget can be live per page.' }) }));
    }
    // The error state has no ErrorBoundary above it (the boundary lives in the
    // settings-gated tree below), so render the same fallback directly.
    // Intercom context is required by ErrorFallback's support button.
    if (settingsError && !settings) {
        return (_jsx(IntercomProvider, { appId: INTERCOM_APP_ID, initializeDelay: 2500, shouldInitialize: intercomReady, children: _jsx(ErrorFallback, { error: settingsError, resetErrorBoundary: retrySettingsFetch }) }));
    }
    if (!appSettings)
        return _jsx(_Fragment, { children: loadingComponent ?? _jsx(WidgetLoading, {}) });
    return (_jsx(IntercomProvider, { appId: INTERCOM_APP_ID, initializeDelay: 2500, shouldInitialize: intercomReady, children: _jsx(SettingsProvider, { initialLayerswapData: appSettings, initialSettings: config?.initialValues, children: _jsx(CallbackProvider, { callbacks: callbacks, children: _jsx(ErrorProvider, { children: _jsx(ErrorBoundary, { FallbackComponent: ErrorFallback, children: _jsx(DescriptorHydrationBoundary, { walletProviders: walletProviders, children: (resolvedProviders) => (_jsx(WalletsProviders, { appName: initialValues?.appName, themeData: themeData, walletProviders: resolvedProviders, children: _jsx(ResolverProviders, { walletProviders: resolvedProviders, children: _jsx(SwapAccountsProvider, { children: _jsx(AsyncModalProvider, { children: children }) }) }) })) }) }) }) }) }) }));
};
/**
 * Owns the descriptor → real-provider transition. Starts with the input
 * array as given; when `loadById` is invoked (by the connect modal or any
 * other consumer of `useWalletDescriptorLoader`), runs the descriptor's
 * lazy import and swaps the resolved provider into the list. Both
 * `WalletsProviders` and `ResolverProviders` re-render with the new list.
 */
const DescriptorHydrationBoundary = ({ walletProviders, children }) => {
    const [loadedById, setLoadedById] = useState(new Map());
    // In-flight loads, deduplicated by id, so concurrent triggers don't double-import the SDK.
    const inflightRef = useRef(new Map());
    // Mirror `loadedById` into a ref so `loadById`/`loadAll` can read the latest
    // resolved set WITHOUT listing it as a dependency. Otherwise every resolved
    // descriptor changes their identity → new context value → all loader
    // consumers re-render (8 cascades when opening the modal).
    const loadedByIdRef = useRef(loadedById);
    loadedByIdRef.current = loadedById;
    const resolvedProviders = useMemo(() => {
        return walletProviders.map(p => {
            if (!isWalletProviderDescriptor(p))
                return p;
            const loaded = loadedById.get(p.id);
            return loaded ?? p;
        });
    }, [walletProviders, loadedById]);
    const loadById = useCallback(async (id) => {
        if (loadedByIdRef.current.has(id))
            return;
        const existing = inflightRef.current.get(id);
        if (existing)
            return existing;
        const descriptor = walletProviders.find(p => isWalletProviderDescriptor(p) && p.id === id);
        if (!descriptor)
            return;
        const p = descriptor.loadProvider().then(real => {
            setLoadedById(prev => {
                if (prev.has(id))
                    return prev;
                const next = new Map(prev);
                next.set(id, real);
                return next;
            });
        }).catch(error => {
            // Absorb the failure so fire-and-forget callers (`void loadById`,
            // `loadAll`'s Promise.all) never produce an unhandled rejection.
            // The in-flight slot is cleared below and `loadedById` stays
            // unset, so the next trigger retries the import.
            console.error(`[layerswap/widget] Failed to load wallet provider "${id}"`, error);
        }).finally(() => {
            inflightRef.current.delete(id);
        });
        inflightRef.current.set(id, p);
        return p;
    }, [walletProviders]);
    const loadAll = useCallback(async () => {
        const pending = walletProviders
            .filter((p) => isWalletProviderDescriptor(p) && !loadedByIdRef.current.has(p.id))
            .map(p => loadById(p.id));
        await Promise.all(pending);
    }, [walletProviders, loadById]);
    // Phase-2 trigger: descriptors whose chain SDK left a persisted-session
    // marker (declared via `hasPersistedSession` — a cheap localStorage sniff
    // that never loads the SDK) hydrate immediately on mount, so restored
    // sessions surface without the user opening the connect modal (the
    // phase-1 `loadAll` trigger). No idle deferral: effects already run after
    // first paint, only descriptors with an actual session marker load
    // (usually zero or one, and their wallet is user-visible state the page
    // is incomplete without), and the connect gate treats these stubs as
    // initializing — every deferred millisecond here is a millisecond the
    // swap form's connect affordances stay in their loading state.
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        walletProviders.forEach(p => {
            if (isWalletProviderDescriptor(p)
                && !loadedByIdRef.current.has(p.id)
                && p.hasPersistedSession?.() === true) {
                void loadById(p.id);
            }
        });
    }, [walletProviders, loadById]);
    const loaderValue = useMemo(() => ({ loadById, loadAll }), [loadById, loadAll]);
    return (_jsx(WalletDescriptorLoaderContext.Provider, { value: loaderValue, children: children(resolvedProviders) }));
};
export const LayerswapProvider = (props) => {
    return (_jsxs(_Fragment, { children: [_jsx(ColorSchema, { themeData: props.config?.theme }), _jsx("div", { style: { backgroundColor: 'transparent', height: '100%', width: '100%' }, className: "layerswap-styles", children: _jsx(LayerswapProviderComponent, { ...props, children: props.children }) })] }));
};
